/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type MatrixClient, type Room } from "matrix-js-sdk";
import {
  Room as LivekitRoom,
  isE2EESupported as isE2EESupportedBrowser,
} from "livekit-client";
import { logger } from "matrix-js-sdk/lib/logger";
import {
  MatrixRTCSessionEvent,
  MembershipManagerEvent,
  Status,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { type CallMembership } from "matrix-js-sdk/lib/matrixrtc/CallMembership";
import { useNavigate } from "react-router-dom";

import { cancelMeetingCapture } from "./meetingCapture";
import { useMeetingAutomation } from "./useMeetingAutomation";
import type { IWidgetApiRequest } from "matrix-widget-api";
import {
  ElementWidgetActions,
  type JoinCallData,
  type WidgetHelpers,
} from "../widget";
import { LobbyView } from "./LobbyView";
import { type MatrixInfo } from "./VideoPreview";
import { PosthogAnalytics } from "../analytics/PosthogAnalytics";
import { useProfile } from "../profile/useProfile";
import { findDeviceByName } from "../utils/media";
import { ActiveCall } from "./InCallView";
import { MUTE_PARTICIPANT_COUNT, type MuteStates } from "./MuteStates";
import { useMediaDevices } from "../MediaDevicesContext";
import { useMatrixRTCSessionMemberships } from "../useMatrixRTCSessionMemberships";
import {
  attemptRTCSessionEntry,
  leaveRTCSession,
  recordUserRTCSessionEntry,
  sendWidgetJoinCall,
} from "../rtcSessionHelpers";
import { useRoomEncryptionSystem } from "../e2ee/roomEncryption";
import { useRoomAvatar } from "./useRoomAvatar";
import { useRoomName } from "./useRoomName";
import { RoomInviteLinkModal } from "./RoomInviteLinkModal";
import { useCanManageRoomInviteLink } from "./useCanManageRoomInviteLink";
import { HeaderStyle, type UrlParams, useUrlParams } from "../UrlParams";
import { E2eeType } from "../e2ee/e2eeType";
import { useAudioContext } from "../useAudioContext";
import {
  callEventAudioSounds,
  type CallEventSounds,
} from "./CallEventAudioRenderer";
import { useLatest } from "../useLatest";
import { usePageTitle } from "../usePageTitle";
import { type ElementCallError } from "../utils/errors.ts";
import { GroupCallErrorBoundary } from "./GroupCallErrorBoundary.tsx";
import { useTypedEventEmitter } from "../useEvents";
import { muteAllAudio$ } from "../state/MuteAllAudioModel.ts";
import { useAppBarTitle } from "../AppBar.tsx";
import { useBehavior } from "../useBehavior.ts";
import { useCallAutoRageshake } from "./useCallAutoRageshake.ts";
import { callTraceJournal, describeTraceError } from "../telemetry";
import { type MatrixRtcOwnMembershipPayload } from "../telemetry/types";
import { areOwnMembershipsSemanticallyEqual } from "../telemetry/membership";
import type { ActiveCallRecoveryResult } from "../recovery/useActiveCallRecovery";
import type {
  TerminalCallError,
  TerminalCallErrorCode,
} from "../recovery/CallRecoveryTypes";
import { CallTerminalView } from "./CallTerminalView";
import {
  useExperimentalToDeviceTransport as useExperimentalToDeviceTransportSetting,
  useNewMembershipManager as useNewMembershipManagerSetting,
  useSetting,
} from "../settings/settings";

declare global {
  interface Window {
    rtcSession?: MatrixRTCSession;
  }
}

let nextUserCallSessionId = 0;

interface UserCallSession {
  readonly id: string;
  readonly rtcSession: MatrixRTCSession;
  readonly standardConnectionFailure: unknown | null;
}

interface SessionTeardownBarrier {
  readonly ownerSessionId: string;
  readonly rtcSession: MatrixRTCSession;
  readonly promise: Promise<void>;
}

function createUserCallSession(rtcSession: MatrixRTCSession): UserCallSession {
  return {
    id: `${rtcSession.room.roomId}:user-call:${++nextUserCallSessionId}`,
    rtcSession,
    standardConnectionFailure: null,
  };
}

function createTerminalCallError(
  code: TerminalCallErrorCode,
  normalizedCode: string,
): TerminalCallError {
  return {
    code,
    normalizedCode,
    stableSignature: `ui_preflight:${normalizedCode}`,
    originalError: null,
  };
}

interface Props {
  client: MatrixClient;
  isPasswordlessUser: boolean;
  confineToRoom: boolean;
  preload: UrlParams["preload"];
  skipLobby: UrlParams["skipLobby"];
  header: HeaderStyle;
  rtcSession: MatrixRTCSession;
  isJoined: boolean;
  muteStates: MuteStates;
  widget: WidgetHelpers | null;
}

export const GroupCallView: FC<Props> = ({
  client,
  isPasswordlessUser,
  confineToRoom,
  preload,
  skipLobby,
  header,
  rtcSession,
  isJoined,
  muteStates,
  widget,
}) => {
  // Used to thread through any errors that occur outside the error boundary
  const [externalError, setExternalError] = useState<ElementCallError | null>(
    null,
  );
  const memberships = useMatrixRTCSessionMemberships(rtcSession);
  const [userCallSession, setUserCallSession] =
    useState<UserCallSession | null>(() =>
      isJoined ? createUserCallSession(rtcSession) : null,
    );
  const userCallSessionRef = useRef<UserCallSession | null>(userCallSession);
  const observedRtcSessionRef = useRef(rtcSession);
  const teardownBarrierRef = useRef<SessionTeardownBarrier | null>(null);
  const [retainedActiveSession, setRetainedActiveSession] =
    useState<MatrixRTCSession | null>(() => (isJoined ? rtcSession : null));
  const [left, setLeft] = useState(false);
  const [recoveryTerminal, setRecoveryTerminal] =
    useState<TerminalCallError | null>(null);
  const terminalBlockedRef = useRef(false);
  const localTeardownOwnersRef = useRef(new Set<string>());
  const recoveryCancelRef = useRef<ActiveCallRecoveryResult["cancel"] | null>(
    null,
  );
  const registerRecoveryCancel = useCallback(
    (cancel: ActiveCallRecoveryResult["cancel"]): (() => void) => {
      recoveryCancelRef.current = cancel;
      return (): void => {
        if (recoveryCancelRef.current === cancel) {
          recoveryCancelRef.current = null;
        }
      };
    },
    [],
  );

  useEffect(() => {
    if (observedRtcSessionRef.current === rtcSession) return;
    observedRtcSessionRef.current = rtcSession;
    userCallSessionRef.current = null;
    terminalBlockedRef.current = false;
    setUserCallSession(null);
    setRetainedActiveSession(null);
    setRecoveryTerminal(null);
  }, [rtcSession]);
  useEffect(() => {
    if (
      !isJoined ||
      left ||
      terminalBlockedRef.current ||
      userCallSessionRef.current !== null
    )
      return;
    // Session могла быть создана до mount (например, восстановление страницы
    // уже активного звонка). User-entry side effects в этом случае уже прошли.
    const session = createUserCallSession(rtcSession);
    userCallSessionRef.current = session;
    setUserCallSession(session);
    setRetainedActiveSession(rtcSession);
  }, [isJoined, left, rtcSession]);

  const muteAllAudio = useBehavior(muteAllAudio$);
  const leaveSoundContext = useLatest(
    useAudioContext({
      sounds: callEventAudioSounds,
      latencyHint: "interactive",
      muted: muteAllAudio,
    }),
  );
  // This should use `useEffectEvent` (only available in experimental versions)
  useEffect(() => {
    if (memberships.length >= MUTE_PARTICIPANT_COUNT)
      muteStates.audio.setEnabled?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update our member event when our mute state changes.
  useEffect(() => {
    if (!isJoined) {
      return;
    }

    const ownMembership = memberships.find(
      (membership) =>
        membership.sender === client.getUserId() &&
        membership.deviceId === client.getDeviceId(),
    );

    if (
      rtcSession.membershipStatus !== Status.Connected ||
      ownMembership === undefined
    ) {
      return;
    }

    void rtcSession
      .updateCallIntent(muteStates.video.enabled ? "video" : "audio")
      .catch((error) => {
        logger.error(
          "[GroupCallView] Не удалось обновить call intent после join",
          error,
          {
            roomId: rtcSession.room.roomId,
            membershipStatus: rtcSession.membershipStatus,
            ownMembership: {
              sender: ownMembership.sender,
              deviceId: ownMembership.deviceId,
              callIntent: ownMembership.callIntent,
            },
            intendedCallIntent: muteStates.video.enabled ? "video" : "audio",
          },
        );
      });
  }, [client, memberships, rtcSession, isJoined, muteStates.video.enabled]);

  useEffect(() => {
    logger.info("[Lifecycle] GroupCallView Component mounted");
    return (): void => {
      logger.info("[Lifecycle] GroupCallView Component unmounted");
    };
  }, []);

  // This CSS is the only way we could find to not make element call scroll for
  // viewport sizes smaller than 122px width. (It is actually this exact number: 122px
  // tested on different devices...)
  useEffect(() => {
    document.body.classList.add("no-scroll-body");
    return (): void => {
      document.body.classList.remove("no-scroll-body");
    };
  }, []);

  useEffect(() => {
    window.rtcSession = rtcSession;
    return (): void => {
      delete window.rtcSession;
    };
  }, [rtcSession]);

  useEffect(() => {
    // Sanity check the room object
    if (client.getRoom(rtcSession.room.roomId) !== rtcSession.room)
      logger.warn(
        `We've ended up with multiple rooms for the same ID (${rtcSession.room.roomId}). This indicates a bug in the group call loading code, and may lead to incomplete room state.`,
      );
  }, [client, rtcSession.room]);

  const room = rtcSession.room as Room;
  const automation = useMeetingAutomation(client, room.roomId);
  useEffect(() => (): void => cancelMeetingCapture(room.roomId), [room.roomId]);
  const { displayName, avatarUrl } = useProfile(client);
  const roomName = useRoomName(room);
  const roomAvatar = useRoomAvatar(room);
  const { returnToLobby } = useUrlParams();
  const e2eeSystem = useRoomEncryptionSystem(room.roomId);
  const encryptMedia = e2eeSystem.kind === E2eeType.PER_PARTICIPANT;
  const [useNewMembershipManager] = useSetting(useNewMembershipManagerSetting);
  const [useExperimentalToDeviceTransport] = useSetting(
    useExperimentalToDeviceTransportSetting,
  );

  usePageTitle(roomName);
  useAppBarTitle(roomName);

  const matrixInfo = useMemo((): MatrixInfo => {
    return {
      userId: client.getUserId()!,
      displayName: displayName!,
      avatarUrl: avatarUrl!,
      roomId: room.roomId,
      roomName,
      roomAlias: room.getCanonicalAlias(),
      roomAvatar,
      e2eeSystem,
    };
  }, [client, displayName, avatarUrl, roomName, room, roomAvatar, e2eeSystem]);

  // Count each member only once, regardless of how many devices they use
  const participantCount = useMemo(
    () => new Set<string>(memberships.map((m) => m.sender!)).size,
    [memberships],
  );
  const autoRageshake = useCallAutoRageshake(room.roomId);

  const mediaDevices = useMediaDevices();
  const latestMuteStates = useLatest(muteStates);

  useTypedEventEmitter(
    rtcSession,
    MatrixRTCSessionEvent.MembershipManagerError,
    (error) => {
      const userId = client.getUserId();
      const deviceId = client.getDeviceId();
      callTraceJournal.record({
        eventType: "matrix_rtc.membership_error",
        stage: "matrix_rtc",
        severity: "error",
        outcome: "failed",
        reasonCode: "membership_manager_error",
        payload: {
          operation: "membership_manager",
          isJoined,
          ownMembershipPresent: memberships.some(
            (membership) =>
              membership.sender === userId && membership.deviceId === deviceId,
          ),
          membershipManagerStatus: rtcSession.membershipStatus ?? null,
          ...describeTraceError(error),
        },
      });
    },
  );

  useTypedEventEmitter(
    rtcSession,
    MembershipManagerEvent.StatusChanged,
    (previous, current) => {
      callTraceJournal.record({
        eventType: "matrix_rtc.membership_status_changed",
        stage: "matrix_rtc",
        severity: current === Status.Disconnected ? "warn" : "info",
        outcome: "state_changed",
        payload: { previous, current },
      });
    },
  );

  useTypedEventEmitter(
    rtcSession,
    MatrixRTCSessionEvent.MembershipsChanged,
    (
      previousMemberships: CallMembership[],
      currentMemberships: CallMembership[],
    ) => {
      const userId = client.getUserId();
      const deviceId = client.getDeviceId();
      const findOwnMembership = (
        values: CallMembership[],
      ): CallMembership | undefined =>
        values.find(
          (membership) =>
            membership.sender === userId && membership.deviceId === deviceId,
        );
      const summarizeOwnMembership = (
        membership: CallMembership | undefined,
      ): MatrixRtcOwnMembershipPayload | null =>
        membership
          ? {
              matrixEventId: membership.eventId ?? null,
              callId: membership.callId,
              callIntent: membership.callIntent ?? null,
              createdAt: membership.createdTs(),
              expiresAt: membership.getAbsoluteExpiry(),
              expiresInMs: membership.getMsUntilExpiry(),
              expired: membership.isExpired(),
              focusSelection: membership.getFocusSelection() ?? null,
              preferredFocusTypes: membership
                .getPreferredFoci()
                .map((focus) => focus.type),
            }
          : null;
      const previousOwn = findOwnMembership(previousMemberships);
      const currentOwn = findOwnMembership(currentMemberships);
      const previousOwnSummary = summarizeOwnMembership(previousOwn);
      const currentOwnSummary = summarizeOwnMembership(currentOwn);
      if (
        areOwnMembershipsSemanticallyEqual(
          previousOwnSummary,
          currentOwnSummary,
        )
      ) {
        return;
      }
      callTraceJournal.record({
        eventType: "matrix_rtc.own_membership_changed",
        stage: "matrix_rtc",
        severity: previousOwn && !currentOwn && isJoined ? "warn" : "info",
        outcome: "state_changed",
        reasonCode:
          previousOwn && !currentOwn
            ? "own_membership_removed"
            : !previousOwn && currentOwn
              ? "own_membership_added"
              : null,
        payload: {
          previousOwn: previousOwnSummary,
          currentOwn: currentOwnSummary,
          membershipManagerStatus: rtcSession.membershipStatus ?? null,
        },
      });
    },
  );

  useTypedEventEmitter(
    rtcSession,
    MembershipManagerEvent.ProbablyLeft,
    (probablyLeft) => {
      callTraceJournal.record({
        eventType: "matrix_rtc.probably_left_changed",
        stage: "matrix_rtc",
        severity: probablyLeft ? "warn" : "info",
        outcome: probablyLeft ? "failed" : "recovered",
        payload: { probablyLeft },
      });
    },
  );

  const beginUserCallSession = useCallback(
    async (session: MatrixRTCSession): Promise<void> => {
      if (userCallSessionRef.current?.rtcSession === session) return;
      const userSession = createUserCallSession(session);
      userCallSessionRef.current = userSession;
      terminalBlockedRef.current = false;
      setLeft(false);
      setRecoveryTerminal(null);
      recordUserRTCSessionEntry(session);

      // Новый coordinator нельзя монтировать, пока старый MatrixRTC leave ещё
      // способен остановить membership той же MatrixRTCSession. Widget Join
      // также ждёт старого Hangup, иначе host увидит события в обратном порядке.
      const barrier = teardownBarrierRef.current;
      if (barrier?.rtcSession === session) {
        await barrier.promise;
      }
      if (userCallSessionRef.current?.id !== userSession.id) return;
      const widgetSideEffect = sendWidgetJoinCall();
      setRetainedActiveSession(session);
      setUserCallSession(userSession);
      try {
        await attemptRTCSessionEntry(
          session,
          encryptMedia,
          useNewMembershipManager,
          useExperimentalToDeviceTransport,
        );
      } catch (error) {
        if (userCallSessionRef.current?.id !== userSession.id) return;
        const failedSession = {
          ...userSession,
          standardConnectionFailure: error,
        };
        userCallSessionRef.current = failedSession;
        setUserCallSession(failedSession);
      }
      await widgetSideEffect;
    },
    [encryptMedia, useExperimentalToDeviceTransport, useNewMembershipManager],
  );

  useEffect(() => {
    const defaultDeviceSetup = async ({
      audioInput,
      videoInput,
    }: JoinCallData): Promise<void> => {
      // XXX: I think this is broken currently - LiveKit *won't* request
      // permissions and give you device names unless you specify a kind, but
      // here we want all kinds of devices. This needs a fix in livekit-client
      // for the following name-matching logic to do anything useful.
      const devices = await LivekitRoom.getLocalDevices(undefined, true);

      if (audioInput) {
        const deviceId = findDeviceByName(audioInput, "audioinput", devices);
        if (!deviceId) {
          logger.warn("Unknown audio input: " + audioInput);
          // override the default mute state
          latestMuteStates.current!.audio.setEnabled?.(false);
        } else {
          logger.debug(
            `Found audio input ID ${deviceId} for name ${audioInput}`,
          );
          mediaDevices.audioInput.select(deviceId);
        }
      }

      if (videoInput) {
        const deviceId = findDeviceByName(videoInput, "videoinput", devices);
        if (!deviceId) {
          logger.warn("Unknown video input: " + videoInput);
          // override the default mute state
          latestMuteStates.current!.video.setEnabled?.(false);
        } else {
          logger.debug(
            `Found video input ID ${deviceId} for name ${videoInput}`,
          );
          mediaDevices.videoInput.select(deviceId);
        }
      }
    };

    if (skipLobby) {
      if (widget && preload) {
        // In preload mode without lobby we wait for a join action before entering
        const onJoin = (ev: CustomEvent<IWidgetApiRequest>): void => {
          callTraceJournal.record({
            eventType: "ui.join_requested",
            stage: "ui",
            outcome: "started",
            payload: { source: "widget_preload" },
          });
          (async (): Promise<void> => {
            await defaultDeviceSetup(ev.detail.data as unknown as JoinCallData);
            await beginUserCallSession(rtcSession);
            widget.api.transport.reply(ev.detail, {});
          })().catch((e) => {
            logger.error("Error joining RTC session on preload", e);
          });
        };
        widget.lazyActions.on(ElementWidgetActions.JoinCall, onJoin);
        return (): void => {
          widget.lazyActions.off(ElementWidgetActions.JoinCall, onJoin);
        };
      } else {
        // No lobby and no preload: we enter the rtc session right away
        callTraceJournal.record({
          eventType: "ui.join_requested",
          stage: "ui",
          outcome: "started",
          payload: { source: "skip_lobby" },
        });
        (async (): Promise<void> => {
          await beginUserCallSession(rtcSession);
        })().catch((e) => {
          logger.error("Error joining RTC session immediately", e);
        });
      }
    }
  }, [
    widget,
    rtcSession,
    preload,
    skipLobby,
    mediaDevices,
    latestMuteStates,
    beginUserCallSession,
  ]);

  const navigate = useNavigate();

  const beginLocalTeardown = useCallback(
    async (
      owner: UserCallSession,
      reason: "terminal" | "host_hangup",
    ): Promise<void> => {
      if (localTeardownOwnersRef.current.has(owner.id)) {
        return teardownBarrierRef.current?.ownerSessionId === owner.id
          ? teardownBarrierRef.current.promise
          : Promise.resolve();
      }
      localTeardownOwnersRef.current.add(owner.id);
      // Terminal/host teardown останавливает только локальный MatrixRTC
      // lifecycle. Widget Hangup/Close принадлежат исключительно user Leave.
      const teardown = owner.rtcSession.leaveRoomSession().then(
        () => undefined,
        (error) => {
          logger.error(`Не удалось завершить MatrixRTC после ${reason}`, error);
        },
      );
      const barrier = {
        ownerSessionId: owner.id,
        rtcSession: owner.rtcSession,
        promise: teardown,
      };
      teardownBarrierRef.current = barrier;
      void teardown.then(() => {
        if (teardownBarrierRef.current === barrier) {
          teardownBarrierRef.current = null;
        }
      });
      return teardown;
    },
    [],
  );

  const onLeave = useCallback(
    (
      cause: "user" | "error" = "user",
      playSound: CallEventSounds = "left",
    ): void => {
      const owner = userCallSessionRef.current;
      if (owner === null) return;
      const audioPromise = leaveSoundContext.current?.playSound(playSound);
      // In embedded/widget mode the iFrame will be killed right after the call ended prohibiting the posthog event from getting sent,
      // therefore we want the event to be sent instantly without getting queued/batched.
      const sendInstantly = !!widget;
      recoveryCancelRef.current?.("user_leave");
      userCallSessionRef.current = null;
      setUserCallSession(null);
      setLeft(true);
      setRetainedActiveSession(null);
      // we need to wait until the callEnded event is tracked on posthog.
      // Otherwise the iFrame gets killed before the callEnded event got tracked.
      const posthogRequest = new Promise((resolve) => {
        PosthogAnalytics.instance.eventCallEnded.track(
          room.roomId,
          rtcSession.memberships.length,
          sendInstantly,
          rtcSession,
        );
        window.setTimeout(resolve, 10);
      });

      const teardown = leaveRTCSession(
        rtcSession,
        cause,
        // Wait for the sound in widget mode (it's not long)
        Promise.all([audioPromise, posthogRequest]),
      )
        // Only sends matrix leave event. The Livekit session will disconnect once the ActiveCall-view unmounts.
        .then(async () => {
          if (
            !isPasswordlessUser &&
            !confineToRoom &&
            !PosthogAnalytics.instance.isEnabled()
          ) {
            await navigate("/");
          }
        })
        .catch((e) => {
          logger.error("Error leaving RTC session", e);
        });
      const barrier = {
        ownerSessionId: owner.id,
        rtcSession,
        promise: teardown,
      };
      teardownBarrierRef.current = barrier;
      void teardown.then(() => {
        if (teardownBarrierRef.current === barrier) {
          teardownBarrierRef.current = null;
        }
      });
    },
    [
      leaveSoundContext,
      widget,
      rtcSession,
      room.roomId,
      isPasswordlessUser,
      confineToRoom,
      navigate,
    ],
  );

  useEffect(() => {
    if (widget && userCallSession?.rtcSession === rtcSession && !left) {
      // set widget to sticky once joined.
      widget.api.setAlwaysOnScreen(true).catch((e) => {
        logger.error("Error calling setAlwaysOnScreen(true)", e);
      });

      const onHangup = (ev: CustomEvent<IWidgetApiRequest>): void => {
        const owner = userCallSession;
        if (userCallSessionRef.current?.id !== owner.id) return;
        widget.api.transport.reply(ev.detail, {});
        recoveryCancelRef.current?.("host_hangup");
        userCallSessionRef.current = null;
        setUserCallSession(null);
        setLeft(true);
        setRetainedActiveSession(null);
        void beginLocalTeardown(owner, "host_hangup");
      };
      widget.lazyActions.once(ElementWidgetActions.HangupCall, onHangup);
      return (): void => {
        widget.lazyActions.off(ElementWidgetActions.HangupCall, onHangup);
      };
    }
  }, [beginLocalTeardown, widget, userCallSession, rtcSession, left]);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const canShareRoom = useCanManageRoomInviteLink(room);
  const onDismissShareModal = useCallback(
    () => setShareModalOpen(false),
    [setShareModalOpen],
  );

  const onShareClickFn = useCallback(
    () => setShareModalOpen(true),
    [setShareModalOpen],
  );
  const onShareClick = canShareRoom ? onShareClickFn : null;

  const browserTerminal =
    !isE2EESupportedBrowser() && e2eeSystem.kind !== E2eeType.NONE
      ? createTerminalCallError(
          "irrecoverable_e2ee",
          "browser:e2ee_unsupported",
        )
      : null;
  useEffect(() => {
    if (browserTerminal === null) return;
    const owner = userCallSessionRef.current;
    if (owner === null) return;
    terminalBlockedRef.current = true;
    userCallSessionRef.current = null;
    setUserCallSession(null);
    setRetainedActiveSession(null);
    void beginLocalTeardown(owner, "terminal");
  }, [beginLocalTeardown, browserTerminal]);

  const shareModal = (
    <RoomInviteLinkModal
      room={room}
      open={shareModalOpen}
      onDismiss={onDismissShareModal}
    />
  );
  const lobbyView = (
    <>
      {shareModal}
      <LobbyView
        client={client}
        matrixInfo={matrixInfo}
        muteStates={muteStates}
        automationControls={automation.controls}
        onEnter={() => {
          automation.prepareEntry();
          void beginUserCallSession(rtcSession);
        }}
        confineToRoom={confineToRoom}
        hideHeader={header === HeaderStyle.None}
        participantCount={participantCount}
        onShareClick={onShareClick}
      />
    </>
  );

  let body: ReactNode;
  if (externalError) {
    // If an error was recorded within this component but outside
    // GroupCallErrorBoundary, create a component that rethrows the error from
    // within the error boundary, so it can be handled uniformly
    const ErrorComponent = (): ReactNode => {
      throw externalError;
    };
    body = <ErrorComponent />;
  } else if (browserTerminal !== null) {
    body = <CallTerminalView error={browserTerminal} widget={widget} />;
  } else if (
    !recoveryTerminal &&
    userCallSession?.rtcSession === rtcSession &&
    (isJoined || (retainedActiveSession === rtcSession && !left))
  ) {
    body = (
      <>
        {shareModal}
        <ActiveCall
          key={userCallSession.id}
          callSessionId={userCallSession.id}
          client={client}
          matrixInfo={matrixInfo}
          rtcSession={rtcSession as MatrixRTCSession}
          matrixRoom={room}
          onLeave={onLeave}
          header={header}
          muteStates={muteStates}
          e2eeSystem={e2eeSystem}
          //otelGroupCallMembership={otelGroupCallMembership}
          onShareClick={onShareClick}
          autoRageshake={autoRageshake}
          registerRecoveryCancel={registerRecoveryCancel}
          onRecoveryTerminal={(error) => {
            if (userCallSessionRef.current?.id !== userCallSession.id) {
              return;
            }
            terminalBlockedRef.current = true;
            setRecoveryTerminal(error);
            userCallSessionRef.current = null;
            setUserCallSession(null);
            setRetainedActiveSession(null);
            void beginLocalTeardown(userCallSession, "terminal");
          }}
          standardConnectionFailure={userCallSession.standardConnectionFailure}
        />
      </>
    );
  } else if (recoveryTerminal) {
    body = <CallTerminalView error={recoveryTerminal} widget={widget} />;
  } else if (left && widget === null) {
    // Left in SPA mode:

    // The call ended view is shown for two reasons: prompting guests to create
    // an account, and prompting users that have opted into analytics to provide
    // feedback. We don't show a feedback prompt to widget users however (at
    // least for now), because we don't yet have designs that would allow widget
    // users to dismiss the feedback prompt and close the call window without
    // submitting anything.
    if (
      isPasswordlessUser ||
      (PosthogAnalytics.instance.isEnabled() && widget === null)
    ) {
      body = lobbyView;
    } else {
      // If the user is a regular user, we'll have sent them back to the homepage,
      // so just sit here & do nothing: otherwise we would (briefly) mount the
      // LobbyView again which would open capture devices again.
      body = null;
    }
  } else if (left && widget !== null) {
    // Left in widget mode:
    body = returnToLobby ? lobbyView : null;
  } else if (preload || skipLobby) {
    // Явный Join уже смонтировал session shell; coordinator выполняет initial pipeline.
    body = null;
  } else {
    body = lobbyView;
  }

  return (
    <GroupCallErrorBoundary
      widget={widget}
      recoveryActionHandler={async (action) => {
        setExternalError(null);
        if (action == "reconnect") {
          callTraceJournal.record({
            eventType: "ui.reconnect_requested",
            stage: "ui",
            outcome: "started",
          });
          callTraceJournal.startManualRejoin();
          setLeft(false);
          await beginUserCallSession(rtcSession);
        }
      }}
      onError={(error) => {
        callTraceJournal.record({
          eventType: "call.fatal_error",
          stage: "call",
          severity: "fatal",
          outcome: "failed",
          reasonCode: "group_call_error_boundary",
          payload: {
            operation: "group_call_error_boundary",
            screen: isJoined ? "InCallView" : "GroupCallView",
            isJoined,
            left,
            preload,
            skipLobby,
            participantCount,
            membershipStatus: rtcSession.membershipStatus ?? null,
            ...describeTraceError(error),
          },
        });
        autoRageshake.reportError(error, {
          screen: isJoined ? "InCallView" : "GroupCallView",
          details: {
            isJoined,
            left,
            preload,
            skipLobby,
            participantCount,
            membershipStatus: rtcSession.membershipStatus,
          },
        });
        if (rtcSession.isJoined()) onLeave("error");
      }}
    >
      {body}
    </GroupCallErrorBoundary>
  );
};
